import { inject, Injectable, REQUEST, RESPONSE_INIT } from '@angular/core';
import { Router } from '@angular/router';

/**
 * Sets the HTTP status of the server-rendered response for the current request.
 *
 * `@angular/ssr` hands the application the very `ResponseInit` it will later build the `Response`
 * from (`RESPONSE_INIT`), and it constructs that `Response` only *after* the render settles — so
 * mutating `status` from a component is what turns a "not found" view into a real 404 instead of a
 * soft 404 (HTTP 200 plus an error page, which crawlers index and SEO audits flag).
 *
 * The token is `null` in the browser and during prerendering/route extraction, where there is no
 * response to set a status on; every call is then a no-op, so callers need no platform check.
 */
@Injectable({ providedIn: 'root' })
export class ResponseStatusService {
    /** The response init `@angular/ssr` will build the `Response` from; `null` off the server. */
    private readonly _responseInit: ResponseInit | null = inject(RESPONSE_INIT);
    /** The SSR request, whose URL is the one the client actually asked for; `null` in the browser. */
    private readonly _request: Request | null = inject(REQUEST);
    private readonly _router: Router = inject(Router);

    /** Sets the status of the SSR response; ignored when there is no server response to set. */
    public set(status: number): void {
        if (!this._responseInit || this._isRedirecting()) {
            return;
        }

        this._responseInit.status = status;
    }

    /**
     * Whether this render will answer with a redirect rather than the page it is rendering.
     *
     * A guard that returns a `UrlTree` does not stop the server render — `@angular/ssr` renders the
     * redirect *target* and only then turns the result into a 3xx. So `/impressum` (redirected to
     * `/de/impressum`) constructs the 404 page, and writing a status there would hand
     * `createRedirectResponse` a 404, which it rejects outright: `Invalid redirect status code`.
     *
     * The requested path and the router's URL diverge in exactly that case, and only in it — the
     * router has already settled on the target by the time components are constructed. Leaving the
     * status alone lets the redirect keep its own 3xx; the client then re-requests the target and
     * gets its real 404 there.
     */
    private _isRedirecting(): boolean {
        if (!this._request) {
            return false;
        }

        return normalizePath(new URL(this._request.url).pathname) !== normalizePath(this._router.url);
    }
}

/** Reduces a URL to a comparable path: no query, no fragment, no trailing slash. */
function normalizePath(url: string): string {
    const path: string = url.split(/[?#]/)[0];

    return path.length > 1 ? path.replace(/\/$/, '') : path;
}
